import { queryClient } from '@/query-client';
import { useMutation, useQuery } from '@tanstack/react-query';

import {
  type AgentRun,
  AgentRunSchema,
  type AgentRuns,
  type AgentRunsQueryOptions,
  AgentRunsSchema,
  type CreateAgentRun,
  type UpdateAgentRun,
} from '@mai/types';

import { apiClient } from './axios';

import { logger } from '@utils/logger';

export const AGENT_RUNS_QUERY_KEY = 'agent-runs';

export const useAgentRunQuery = (
  agentRunId: string,
  enabled: boolean = true,
) => {
  return useQuery({
    queryKey: [AGENT_RUNS_QUERY_KEY, agentRunId],
    queryFn: async () => {
      const response = await apiClient.get<AgentRun>(
        `/agent-runs/${agentRunId}`,
      );
      const validatedData = AgentRunSchema.safeParse(response.data);
      if (!validatedData.success) {
        logger.warn('failed to validate agent run data', {
          error: validatedData.error,
        });
        return response.data;
      }
      return validatedData.data;
    },
    enabled,
  });
};

export const useAgentRunsQuery = (
  options: AgentRunsQueryOptions,
  enabled: boolean = true,
) => {
  return useQuery({
    queryKey: [AGENT_RUNS_QUERY_KEY, options],
    queryFn: async () => {
      const response = await apiClient.get<AgentRuns>('/agent-runs', {
        params: options,
      });
      const validatedData = AgentRunsSchema.safeParse(response.data);
      if (!validatedData.success) {
        logger.warn('failed to validate agent runs data', {
          error: validatedData.error,
        });
        return response.data;
      }
      return validatedData.data;
    },
    enabled,
  });
};

export const createAgentRun = async (agentRun: CreateAgentRun) => {
  const response = await apiClient.post<AgentRun>('/agent-runs', agentRun);
  const validatedData = AgentRunSchema.safeParse(response.data);
  if (!validatedData.success) {
    logger.warn('failed to validate agent run data', {
      error: validatedData.error,
    });
    return response.data;
  }
  return validatedData.data;
};

export const useCreateAgentRunMutation = () => {
  return useMutation({
    mutationFn: createAgentRun,
  });
};

export const updateAgentRun = async (
  agentRunId: string,
  data: UpdateAgentRun,
) => {
  const response = await apiClient.patch<AgentRun>(
    `/agent-runs/${agentRunId}`,
    data,
  );
  const validatedData = AgentRunSchema.safeParse(response.data);
  if (!validatedData.success) {
    logger.warn('failed to validate agent run data', {
      error: validatedData.error,
    });
    return response.data;
  }
  await queryClient.invalidateQueries({
    queryKey: [AGENT_RUNS_QUERY_KEY, agentRunId],
  });
  await queryClient.invalidateQueries({
    queryKey: [AGENT_RUNS_QUERY_KEY],
  });
  return validatedData.data;
};

export const useUpdateAgentRunMutation = () => {
  return useMutation({
    mutationFn: ({
      agentRunId,
      data,
    }: {
      agentRunId: string;
      data: UpdateAgentRun;
    }) => updateAgentRun(agentRunId, data),
  });
};
